import React, { useState } from 'react';

interface PlayingCardProps {
  suit?: string;
  rank?: string;
  isBack?: boolean;
  onClick?: () => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  rotation?: number;
  isSelected?: boolean;
  isPlayable?: boolean;
  isPlaying?: boolean;
  isDealing?: boolean;
  isPowerCard?: boolean;
  isInvalid?: boolean;
  isFloating?: boolean;
  isCollecting?: boolean;
  dealDelay?: number;
  aiPlayerColor?: 'blue' | 'red' | 'purple' | 'green';
  onHover?: (isHovering: boolean) => void;
  playOffsetX?: number;
  playRotation?: number;
  disabled?: boolean;
}

const getSuitColor = (suit: string): string => {
  const suitColors: Record<string, string> = {
    '♥': '#D92D2D',
    '♦': '#D92D2D', 
    '♣': '#1E293B',
    '♠': '#1E293B',
    'hearts': '#D92D2D',
    'diamonds': '#D92D2D',
    'clubs': '#1E293B',
    'spades': '#1E293B'
  };
  return suitColors[suit] || '#1E293B';
};

const getSuitSymbol = (suit: string): string => {
  const suitSymbols: Record<string, string> = {
    'hearts': '♥',
    'diamonds': '♦',
    'clubs': '♣',
    'spades': '♠',
    '♥': '♥',
    '♦': '♦',
    '♣': '♣',
    '♠': '♠'
  };
  return suitSymbols[suit] || suit;
};

const PlayingCard: React.FC<PlayingCardProps> = ({
  suit = 'spades',
  rank = 'A',
  // isBack = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  onClick,
  className = '',
  size = 'medium',
  rotation = 0,
  isSelected = false,
  isPlayable = false,
  isPlaying = false,
  isDealing = false,
  isPowerCard = false,
  isInvalid = false,
  isFloating = false,
  isCollecting = false,
  // dealDelay = 0, // eslint-disable-line @typescript-eslint/no-unused-vars
  onHover,
  disabled = false
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setIsHovered] = useState(false);

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
      onHover?.(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onHover?.(false);
  };

    const sizeClasses = {
      small: 'w-6 h-9 text-xs',
      medium: 'w-8 h-12 text-xs', 
      large: 'w-12 h-18 text-sm'
  };

    const dims = {
      small: {
        width: '48px',
        height: '70px',
        cornerOffset: '5px',
        cornerFont: '0.6rem',
        centerFont: '1rem',
        borderWidth: '1.5px',
        borderRadius: '6px',
        copyBadgeSize: '12px',
        copyBadgeFont: '9px'
      },
      medium: {
        width: '72px',
        height: '104px',
        cornerOffset: '8px',
        cornerFont: '0.95rem',
        centerFont: '2.2rem',
        borderWidth: '3px',
        borderRadius: '12px'
      },
      large: {
        width: '96px',
        height: '144px',
        cornerOffset: '10px',
        cornerFont: '1.2rem',
        centerFont: '2.8rem',
        borderWidth: '4px',
        borderRadius: '14px'
      }
    }[size];

  const cardClasses = [
    'playing-card-container',
    sizeClasses[size],
    className,
    isSelected ? 'selected' : '',
    isPlayable ? 'playable' : '',
    isInvalid ? 'invalid' : '',
    isPlaying ? 'card-playing' : '',
    isDealing ? 'card-dealing' : '',
    isFloating ? 'card-floating' : '',
    isCollecting ? 'trick-collect' : '',
    isPowerCard ? 'power-card' : '',
    onClick ? 'cursor-pointer' : ''
  ].filter(Boolean).join(' ');

  // Simple, always-front card design (no flipping)
  return (
    <div
      className={cardClasses}
      style={{
        width: dims.width,
        height: dims.height,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        perspective: '1000px',
        cursor: onClick ? 'pointer' : 'default',
        transform: `rotate(${rotation}deg)`,
        transition: 'all 0.3s ease'
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        background: 'white',
        border: `${dims.borderWidth} solid ${isSelected ? '#3B82F6' : isPlayable ? '#10B981' : isInvalid ? '#EF4444' : '#000000'}`,
        borderRadius: dims.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: isSelected ? '0 0 20px rgba(59, 130, 246, 0.4)' : 
                   isPlayable ? '0 0 20px rgba(16, 185, 129, 0.4)' : 
                   isInvalid ? '0 0 20px rgba(239, 68, 68, 0.4)' :
                   '0 4px 12px rgba(0, 0, 0, 0.3)',
        overflow: 'visible'
      } as React.CSSProperties}>
        
        {/* Top-left corner */}
        <div style={{
          position: 'absolute',
          top: dims.cornerOffset,
          left: dims.cornerOffset,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: dims.cornerFont,
          fontWeight: 'bold',
          lineHeight: '1',
          color: getSuitColor(suit || '')
        }}>
          <div>{rank || '?'}</div>
          <div>{getSuitSymbol(suit || '') || '?'}</div>
        </div>
        
        {/* Bottom-right corner (rotated) */}
        <div style={{
          position: 'absolute',
          bottom: dims.cornerOffset,
          right: dims.cornerOffset,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: dims.cornerFont,
          fontWeight: 'bold',
          lineHeight: '1',
          color: getSuitColor(suit || ''),
          transform: 'rotate(180deg)'
        }}>
          <div>{rank || '?'}</div>
          <div>{getSuitSymbol(suit || '') || '?'}</div>
        </div>
        
        {/* Center symbol */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: dims.centerFont,
          fontWeight: 'bold',
          color: getSuitColor(suit || '')
        }}>
          {getSuitSymbol(suit || '') || '❓'}
        </div>

        {/* No copyNumber badge - copyNumber logic removed project-wide */}
      </div>
    </div>
  );
};

export default PlayingCard;